/** Sorteio de loot: drop de equipamento por inimigo e conteúdo de baús. Puro (sem mundo). */
import { GameConfig } from "../config/GameConfig";
import { allDefs, isWeaponDef } from "./Catalog";
import { rollRarity, capRarityByLevel, maxArmorTierForLevel, rarityIndex, type RaritySource } from "./Rarity";
import { createInstance } from "./ItemFactory";
import { ESSENCE_TYPES, type EssenceType } from "../essence/EssenceTypes";
import type { ArmorDefinition, EquipmentInstance, Rarity, WeaponDefinition } from "./EquipmentTypes";
import { randRange } from "../utils/math";

const DEFS = allDefs();
// pool de drop NORMAL: exclui armas bossOnly (só caem de boss, §10.4P)
const NORMAL_DEFS = DEFS.filter((d) => !(isWeaponDef(d) && (d as WeaponDefinition).bossOnly) && !(d as { raidOnly?: boolean }).raidOnly); // 10.9GM — raidOnly (Coração do Titã) NÃO cai de monstro/baú
// armas EXCLUSIVAS de boss
const BOSS_WEAPONS = DEFS.filter((d): d is WeaponDefinition => isWeaponDef(d) && !!(d as WeaponDefinition).bossOnly && !(d as WeaponDefinition).raidOnly); // 10.9GG — raidOnly fora do pool de boss normal
const RAID_UNIQUES = DEFS.filter((d) => !!(d as { raidOnly?: boolean }).raidOnly); // 10.9GG — só do boss GLOBAL
const ARMOR_DEFS = DEFS.filter((d) => !isWeaponDef(d) && !(d as { raidOnly?: boolean }).raidOnly); // 10.9GM — armaduras/acessórios SEM os únicos da raid

/** Pool respeitando os TIPOS de arma liberados na região (10.5F). Armaduras sempre entram;
 * armas só se o weaponType estiver em `allowedTypes` (undefined = todas). */
function poolFor(allowedTypes?: string[]): typeof NORMAL_DEFS {
  if (!allowedTypes) return NORMAL_DEFS;
  const set = new Set(allowedTypes);
  const p = NORMAL_DEFS.filter((d) => !isWeaponDef(d) || set.has((d as WeaponDefinition).weaponType));
  return p.length > 0 ? p : NORMAL_DEFS;
}

function randomInstance(source: RaritySource, rarity?: Rarity, allowedTypes?: string[], playerLevel = 1): EquipmentInstance | null {
  const base = poolFor(allowedTypes);
  // 10.9AN — armadura de TIER acima do nível NÃO cai (só armas eram filtradas por região; um nv12
  // pegava peitoral T3 — 2.3× o T1 — e virava tanque). T2 a partir do nv20, T3 do nv35.
  const maxTier = maxArmorTierForLevel(playerLevel);
  const pool = base.filter((d) => isWeaponDef(d) || ((d as ArmorDefinition).tier ?? 1) <= maxTier);
  const src = pool.length > 0 ? pool : base;
  const def = src[Math.floor(Math.random() * src.length)];
  return createInstance(def.id, rarity ?? rollRarity(source));
}

/** Arma EXCLUSIVA de boss (não cai de monstro normal). Raridade alta por padrão. */
export function rollBossWeapon(rarity: Rarity = "epic", allowedTypes?: string[], playerLevel?: number): EquipmentInstance | null {
  if (BOSS_WEAPONS.length === 0) return null;
  // 10.9CV — arma de boss do TIPO que a classe usa (mago = cajado, arqueiro = arco, etc.)
  let pool = allowedTypes && allowedTypes.length
    ? BOSS_WEAPONS.filter((d) => allowedTypes.includes(d.weaponType))
    : BOSS_WEAPONS.slice();
  if (pool.length === 0) pool = BOSS_WEAPONS.slice();
  // 10.9FZ — tier-gate: só o MAIOR tier que o nível permite (evita dropar arma que não dá pra usar)
  if (typeof playerLevel === "number") {
    const maxT = maxArmorTierForLevel(playerLevel);
    const gated = pool.filter((d) => ((d as { tier?: number }).tier ?? 1) <= maxT);
    const top = gated.filter((d) => ((d as { tier?: number }).tier ?? 1) === Math.max(...gated.map((x) => (x as { tier?: number }).tier ?? 1)));
    if (top.length > 0) pool = Math.random() < 0.6 ? top : gated; // 60% na melhor faixa
  }
  const def = pool[Math.floor(Math.random() * pool.length)];
  return createInstance(def.id, rarity);
}

const RARITY_ORDER = GameConfig.rarity.order;
type DropBucket = "none" | Rarity;

/** Sorteia UM balde da tabela de drop (pesos relativos; inclui "none" = só dinheiro). */
function pickBucket(table: Record<string, number>): DropBucket {
  let total = 0;
  for (const k in table) total += table[k];
  let r = Math.random() * total;
  for (const k in table) {
    r -= table[k];
    if (r <= 0) return k as DropBucket;
  }
  return "none";
}
function betterBucket(a: DropBucket, b: DropBucket): DropBucket {
  const ia = a === "none" ? -1 : RARITY_ORDER.indexOf(a);
  const ib = b === "none" ? -1 : RARITY_ORDER.indexOf(b);
  return ib > ia ? b : a;
}

/**
 * Drop ao matar um inimigo: sorteia 1 resultado da dropTable da fonte. "none" = só dinheiro
 * (essência), nenhum item; senão retorna 1 item DAQUELA raridade (nunca mais de um).
 * `boost` (Rift Surge, §38): sorteia 2x e mantém a MELHOR raridade — continua 1 item só.
 */
export function rollEnemyDrop(enemyId: string, boost = false, allowedTypes?: string[], playerLevel = 1): EquipmentInstance | null {
  const source = (GameConfig.loot.raritySource as Record<string, string>)[enemyId] ?? "normal";
  const tables = GameConfig.loot.dropTable as Record<string, Record<string, number>>;
  const table = tables[source] ?? tables.normal;
  let bucket = pickBucket(table);
  if (boost) bucket = betterBucket(bucket, pickBucket(table));
  if (bucket === "none") return null;
  // 10.6V — cap por nível: sem legendária pra lv 5 (o player não conseguiria equipar)
  bucket = capRarityByLevel(bucket, playerLevel);
  // 10.9CQ — armas só caem do TIPO que a CLASSE usa (mago = cajado, arqueiro = arco, etc.). Antes
  // era filtrado por REGIÃO (10.5F), então o mago nunca recebia cajado. Armaduras não são afetadas.
  return randomInstance(source as RaritySource, bucket, allowedTypes, playerLevel);
}

export interface ChestLoot {
  items: EquipmentInstance[];
  essence: { type: EssenceType; amount: number };
}

/**
 * `guaranteed` (10.9K2): o 1º item sai com PELO MENOS essa raridade (os outros sorteiam normal) —
 * é o que o nome do baú premium da loja promete ("Baú Lendário"). O teto por nível continua valendo.
 */
export function rollChest(type: "wooden" | "void" | "alpha" | "ruined" | "shard" | "colossus" | "skyruin" | "tempest", playerLevel = 1, allowedTypes?: string[], guaranteed?: Rarity): ChestLoot {
  const cfg = GameConfig.chests[type];
  const source = cfg.raritySource as RaritySource;
  // quantidade por tipo: baús maiores rendem mais itens
  const count =
    type === "wooden" || type === "ruined" ? 1 + (Math.random() < 0.4 ? 1 : 0) : type === "void" || type === "shard" ? 1 + (Math.random() < 0.5 ? 1 : 0) : type === "colossus" ? 3 : 2;
  const items: EquipmentInstance[] = [];
  for (let i = 0; i < count; i++) {
    const rolled = rollRarity(source);
    const raw = i === 0 && guaranteed && rarityIndex(rolled) < rarityIndex(guaranteed) ? guaranteed : rolled;
    const capped = capRarityByLevel(raw, playerLevel); // 10.6V
    const it = randomInstance(source, capped, allowedTypes, playerLevel); // 10.9AN tier de armadura + 10.9CQ arma pela classe
    if (it) items.push(it);
  }
  const eType = ESSENCE_TYPES[Math.floor(Math.random() * ESSENCE_TYPES.length)];
  const amount = Math.round(randRange(cfg.essence.min, cfg.essence.max));
  return { items, essence: { type: eType, amount } };
}

/** 10.9FX — armadura de BOSS: a peça do MAIOR tier que o nível do Caçador permite (Eclipse T7 no Nv150). */
export function rollBossArmor(rarity: Rarity, playerLevel: number): EquipmentInstance | null {
  const maxTier = maxArmorTierForLevel(playerLevel);
  const pool = ARMOR_DEFS.filter((d) => ((d as { tier?: number }).tier ?? 1) === maxTier && !(d as { protects?: string }).protects);
  const src = pool.length > 0 ? pool : ARMOR_DEFS.filter((d) => !(d as { protects?: string }).protects);
  if (src.length === 0) return null;
  const def = src[Math.floor(Math.random() * src.length)];
  return createInstance(def.id, rarity);
}

/** 10.9GG — item ÚNICO do BOSS GLOBAL (só cai na raid). Prefere a arma do TIPO da classe; senão sorteia geral. */
export function rollRaidUnique(rarity: Rarity, allowedTypes?: string[]): EquipmentInstance | null {
  if (RAID_UNIQUES.length === 0) return null;
  let pool = RAID_UNIQUES;
  if (allowedTypes && allowedTypes.length) {
    const set = new Set(allowedTypes);
    const cls = RAID_UNIQUES.filter((d) => !isWeaponDef(d) || set.has((d as WeaponDefinition).weaponType));
    if (cls.length > 0) pool = cls;
  }
  const def = pool[Math.floor(Math.random() * pool.length)];
  return createInstance(def.id, rarity);
}
